/**
 * AddCardLink — public durable add-card landing page.
 *
 * Coaches email/share /add-card/{token}. This page mints a FRESH Stripe
 * Checkout setup session on demand, so the link never goes stale (raw
 * Stripe session URLs expire after 24h — the old emails embedded those
 * and parents hit "link expired"). No sign-in required.
 */
import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CreditCard, ShieldCheck, Lock, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AddCardLink() {
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const done = searchParams.get("done") === "1";
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [firstName, setFirstName] = useState("");

    const startCheckout = async () => {
        setBusy(true);
        setError(null);
        try {
            const r = await axios.post(`${API}/billing/card-link/${token}/session`, {
                origin_url: window.location.origin,
            });
            if (r.data?.first_name) setFirstName(r.data.first_name);
            window.location.href = r.data.url;
        } catch (e) {
            setError(e?.response?.data?.detail || "Couldn't open the secure card form. Please try again.");
            setBusy(false);
        }
    };

    useEffect(() => {
        if (!done) startCheckout();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div data-testid="add-card-link-page" className="min-h-screen bg-brand-obsidian text-white flex items-center justify-center px-6">
            <div className="max-w-lg w-full border border-zinc-800 rounded-2xl bg-zinc-950/70 p-8 text-center">
                {done ? (
                    <div data-testid="add-card-link-success">
                        <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                        <h1 className="font-display text-3xl tracking-wider uppercase">Card saved</h1>
                        <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
                            You're all set{firstName ? `, ${firstName}` : ""} — your card is securely on file with Stripe.
                            Your coach can now set up sessions, packs, or membership. Nothing was charged today.
                        </p>
                        <Link to="/" className="inline-block mt-6 text-[11px] tracking-[0.25em] uppercase text-zinc-400 hover:text-white">
                            Back to maxsc.net →
                        </Link>
                    </div>
                ) : error ? (
                    <div data-testid="add-card-link-error">
                        <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-4" />
                        <h1 className="font-display text-2xl tracking-wider uppercase">Couldn't open card form</h1>
                        <p className="text-sm text-zinc-300 mt-3 leading-relaxed">{error}</p>
                        <button
                            type="button"
                            data-testid="add-card-link-retry"
                            onClick={startCheckout}
                            disabled={busy}
                            className="mt-6 w-full text-sm tracking-[0.15em] uppercase px-6 py-3.5 border border-brand-red bg-brand-red text-white hover:bg-brand-red/90 inline-flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                            Try again
                        </button>
                    </div>
                ) : (
                    <div data-testid="add-card-link-loading">
                        <Loader2 className="w-10 h-10 text-brand-red animate-spin mx-auto mb-4" />
                        <h1 className="font-display text-2xl tracking-wider uppercase">Opening secure card form…</h1>
                        <p className="text-sm text-zinc-300 mt-3 leading-relaxed">
                            Taking you to Stripe's secure checkout. <b>$0 charged today</b> — this just saves your
                            card on file at Maximum Sports Conditioning.
                        </p>
                        <ul className="mt-5 space-y-2 text-left text-[12px] text-zinc-400">
                            <li className="flex items-start gap-2">
                                <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                                PCI-compliant Stripe vault — we never see your card number.
                            </li>
                            <li className="flex items-start gap-2">
                                <Lock className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                                Update or remove your card any time.
                            </li>
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}
